import { useState, useCallback } from 'react';
import { llmService } from '../services/api';

export interface LLMParseResult {
  amount_vnd?: number;
  amount_usd?: number;
  tax_percent?: number;
  tax_amount_vnd?: string;
  tax_amount_usd?: string;
  currency_detected?: 'VND' | 'USD';
  suggested_category_id?: number;
  cleaned_description?: string;
  confidence?: number;
}

export interface LLMParsingState {
  isLoading: boolean;
  error: string | null;
  lastParsedDescription: string;
  parseResult: LLMParseResult | null;
  confidence: number;
}

export interface LLMParsingOptions {
  minDescriptionLength?: number;
  debounceMs?: number;
  minConfidence?: number;
  recordType?: string;
  accountType?: string;
  onParseStart?: () => void;
  onParseComplete?: (result: LLMParseResult) => void;
  onParseError?: (error: string) => void;
}

export const useLLMParsing = (options: LLMParsingOptions = {}) => {
  const {
    minDescriptionLength = 10,
    minConfidence = 0.7,
    recordType,
    accountType,
    onParseStart,
    onParseComplete,
    onParseError
  } = options;

  const [state, setState] = useState<LLMParsingState>({
    isLoading: false,
    error: null,
    lastParsedDescription: '',
    parseResult: null,
    confidence: 0
  });

  const parseDescription = useCallback(async (description: string): Promise<LLMParseResult | null> => {
    // Skip if description is too short or same as last parsed
    if (!description.trim() || 
        description.length < minDescriptionLength || 
        description === state.lastParsedDescription) {
      return null;
    }

    setState(prev => ({
      ...prev,
      isLoading: true,
      error: null
    }));

    onParseStart?.();

    try {
      const response = await llmService.parseDescription(description, recordType, accountType);
      
      if (response.data.success && response.data.data) {
        const result = response.data.data;
        
        setState(prev => ({
          ...prev,
          isLoading: false,
          parseResult: result,
          confidence: result.confidence || 0,
          lastParsedDescription: description,
          error: null
        }));

        // Only call onParseComplete if confidence is high enough
        if (result.confidence >= minConfidence) {
          onParseComplete?.(result);
        }

        return result;
      } else {
        // Service disabled or no result
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: response.data.message || 'LLM service unavailable',
          parseResult: null,
          confidence: 0
        }));
        
        return null;
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to parse description';
      
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
        parseResult: null,
        confidence: 0
      }));

      onParseError?.(errorMessage);
      return null;
    }
  }, [minDescriptionLength, minConfidence, state.lastParsedDescription, onParseStart, onParseComplete, onParseError]);

  const clearState = useCallback(() => {
    setState({
      isLoading: false,
      error: null,
      lastParsedDescription: '',
      parseResult: null,
      confidence: 0
    });
  }, []);

  const isServiceAvailable = useCallback(() => {
    // Check if we have a non-error state or if we haven't tried yet
    return state.error !== 'LLM service is disabled' && state.error !== 'LLM service unavailable';
  }, [state.error]);

  return {
    // State
    isLoading: state.isLoading,
    error: state.error,
    parseResult: state.parseResult,
    confidence: state.confidence,
    lastParsedDescription: state.lastParsedDescription,
    
    // Actions
    parseDescription,
    clearState,
    isServiceAvailable
  };
}; 